// src/utils/enFabrication.ts — SCE Issues
// Les équipements en cours de fabrication (05/10), et ceux que seul leur
// dossier de production connaît : sur stock, en vente directe (07/10).
//
// ─── D'où vient la liste ───
//
// De l'arborescence de production sur le réseau :
//
//   M:\FR06-Equipement-Manufacturing-Dossiers-Production\1. Dossiers-Electroniques
//     ├── 1-Document aide pour remplissage        ← dossiers de suivi : ignorés
//     ├── 8-FAT
//     ├── 4725-H - PN3425 - CVE-2501-0055 - HF PROCESS M   ← un équipement
//     ├── 4925-B - SALES - CVE-2601-0025 - PUL
//     └── 4947-SC - STOCK - OF-2603-0246 - HP 110
//
// Un sous-dossier qui COMMENCE par un N° de série = un équipement en
// fabrication ; terminé, il est déplacé ailleurs (Guillaume, 05/10). Seuls les
// noms des sous-dossiers comptent, jamais leur contenu.
//
// ─── Ce que dit le nom (07/10) ───
//
//   « N°SERIE - PN - CV-xxxx-xxxx - nom eqpt defini selon tableau AIC2 »
//
// Le 2e morceau est un PN, ou « STOCK » (fabriqué, testé, stocké avant d'être
// vendu : le 3e morceau est alors l'OF), ou « SALES » (vente directe : le 3e
// est le CVE). Sans projet, ces équipements n'arrivent pas par l'export
// Windchill : le dossier est le seul endroit où ils existent. Absents du
// référentiel, ils y sont AJOUTÉS (choix de Guillaume, 07/10) :
//  - STOCK : OF, PN « STOCK » (regroupés dans /equipements ; s'il repasse en
//    atelier sur un projet, l'import Windchill remplace STOCK par le vrai PN),
//    modèle ;
//  - SALES : CVE et modèle, sans OF ni PN.
// Le modèle se lit dans le nom d'équipement AIC2 : « HP 110 » = Hipersep
// Prochrom 110, « HF PROCESS M », « RP 450 », « PUL », « SLURRY 220L ».
//
// Le navigateur ne lit pas le réseau tout seul : l'admin le lui ouvre depuis
// /admin, juste après l'export Windchill (un seul import), voit l'aperçu, puis
// valide. La liste est rangée dans le bloc `enFabrication` de la ligne
// `__global__` de SCE_UserPrefs : aucune colonne SharePoint. Si un jour ces
// dossiers passent dans SharePoint, seule la lecture changera.
//
// Pur : aucun réseau, aucun React.

import {
  CATEGORIES_PAR_SUFFIXE,
  cleRapprochement,
  suffixeNumeroSerie,
  type CategorieEquipement,
} from '@/types/issue.types'
import { identifierModele } from '@/config/equipements.catalogue'
import { extraireCve, extraireOf } from '@/utils/windchillCsv'
import { modeleAdmis } from '@/utils/modelesManuels'

export const VERSION_EN_FABRICATION = 1

export interface ListeEnFabrication {
  version: number
  /** N° de série, tels qu'écrits dans le nom des dossiers. */
  numerosSerie: string[]
  /** Date ISO de la mise à jour. */
  le: string
  /** Qui l'a faite (nom affiché). */
  par: string
}

/** « 4725-H - PN3425 - … » → « 4725-H » ; « 1245-H-06 » garde son indice ; sinon null. */
export function numeroSerieDeDossier(nom: string): string | null {
  const m = /^\s*(\d{3,5}-[A-Za-z]{1,3}(?:-\d{1,2})?)(?=\s|$)/.exec(nom)
  return m?.[1]?.toUpperCase() ?? null
}

/** Projet (PN), sur stock, ou vente directe ; null = rien de tout ça dans le nom. */
export type OrigineDossier = 'projet' | 'stock' | 'vente'

export interface NomDossierLu {
  numeroSerie: string
  origine: OrigineDossier | null
  pn: string
  cve: string
  of: string
  /** Le nom d'équipement de fin (« HP 110 »), selon le tableau AIC2. */
  libelle: string
}

/**
 * « 4947-SC - STOCK - OF-2603-0246 - HP 110 » → ses morceaux. Chacun est
 * reconnu à sa forme, pas à sa place : un dossier dont l'ordre varie se lit
 * quand même. Ce qui n'est ni PN, ni STOCK / SALES, ni CVE, ni OF est le nom
 * d'équipement. Null si le nom ne commence pas par un N° de série.
 */
export function lireNomDossier(nom: string): NomDossierLu | null {
  const numeroSerie = numeroSerieDeDossier(nom)
  if (!numeroSerie) return null
  const lu: NomDossierLu = { numeroSerie, origine: null, pn: '', cve: '', of: '', libelle: '' }
  const reste: string[] = []
  // « 1245-H-06 » n'a pas d'espaces autour de ses tirets : il reste entier.
  for (const morceau of nom.trim().split(/\s+-\s+/).slice(1)) {
    const m = morceau.trim()
    const haut = m.toUpperCase()
    if (/^PN\s*\d{3,5}$/.test(haut)) {
      lu.pn = haut.replace(/\s+/g, '')
      lu.origine = 'projet'
    } else if (haut === 'STOCK') lu.origine = 'stock'
    else if (haut === 'SALES') lu.origine = 'vente'
    else if (!lu.cve && extraireCve(m)) lu.cve = extraireCve(m)
    else if (!lu.of && extraireOf(m)) lu.of = extraireOf(m)
    else if (m !== '') reste.push(m)
  }
  lu.libelle = reste.join(' - ')
  return lu
}

/**
 * Le modèle du catalogue d'après le nom d'équipement AIC2. Les deux lettres de
 * tête disent gamme et famille (H Hipersep, R Resolute ; F Flowdrive, P
 * Prochrom), la suite le calibre ; le reste (« PUL », « SLURRY 220L ») se lit
 * comme une fourniture Windchill. Un modèle qui ne va pas avec le suffixe du
 * N° de série (« RF BioSC L » sur un -SC) est écarté : null, colonne vide.
 */
export function modeleDuLibelleDossier(libelle: string, categorie: CategorieEquipement | null): string | null {
  const t = libelle.trim()
  const m = /^([HR])([FP])\s+(.+)$/i.exec(t)
  const texte = m
    ? `${m[1]?.toUpperCase() === 'H' ? 'Hipersep' : 'Resolute'} ${m[2]?.toUpperCase() === 'F' ? 'Flowdrive' : 'Prochrom'} ${m[3] ?? ''}`
    : t
  const modele = identifierModele(texte)
  return modele && modeleAdmis(modele.libelle, categorie) ? modele.libelle : null
}

/** PN écrit pour un équipement sur stock. */
export const PN_STOCK = 'STOCK'

/** Un équipement sur stock ou en vente directe, absent du référentiel : à y ajouter. */
export interface AjoutDepuisDossier {
  numeroSerie: string
  origine: 'stock' | 'vente'
  of: string
  /** PN_STOCK pour un stock, vide pour une vente directe. */
  pn: string
  cve: string
  /** Libellé exact du catalogue, ou null (colonne Modèle laissée vide). */
  modele: string | null
  /** Le nom d'équipement lu (« HP 110 »), pour l'aperçu. */
  libelle: string
  categorie: CategorieEquipement
  dossier: string
}

/**
 * Une liste collée : « Copier en tant que chemin » de l'Explorateur donne
 * `"M:\…\4725-H - PN3425 - …"` par ligne. On garde le dernier segment, sans
 * guillemets. Une ligne qui n'est qu'un nom de dossier passe telle quelle.
 */
export function nomsDepuisTexte(texte: string): string[] {
  return texte
    .split(/\r?\n/)
    .map((l) => l.trim().replace(/^"+|"+$/g, '').replace(/[\\/]+$/, ''))
    .map((l) => l.split(/[\\/]/).pop() ?? '')
    .map((l) => l.trim())
    .filter((l) => l !== '')
}

export interface AnalyseDossiers {
  /** N° de série lus, sans doublon, dans l'ordre. */
  numeros: string[]
  /** Nouveaux par rapport à la liste enregistrée. */
  entrent: string[]
  /** Dans la liste enregistrée, plus dans les dossiers. */
  sortent: string[]
  /**
   * Lus mais absents du référentiel, ni sur stock ni en vente directe : ils
   * arriveront par un prochain export Windchill.
   */
  horsReferentiel: string[]
  /** Sur stock ou en vente directe, absents du référentiel : ajoutés à la validation. */
  aAjouter: AjoutDepuisDossier[]
  /** Sur stock ou en vente directe, absents, mais pas ajoutables (OF manquant ou déjà pris). */
  nonAjoutes: { numeroSerie: string; dossier: string; motif: string }[]
  /** Dossiers qui ne commencent pas par un N° de série (suivi, formation…). */
  ignores: string[]
}

/**
 * @param connus le référentiel ET les lignes de l'export Windchill importé en
 *        même temps : un équipement qui arrive par Windchill n'est pas ajouté
 *        depuis son dossier, Windchill en sait plus.
 * @param actuels la liste « en fabrication » enregistrée.
 */
export function analyserDossiers(
  noms: readonly string[],
  connus: readonly { numeroSerie: string; of: string }[],
  actuels: readonly string[],
): AnalyseDossiers {
  const numeros: string[] = []
  const lus: { lu: NomDossierLu; dossier: string }[] = []
  const vus = new Set<string>()
  const ignores: string[] = []
  for (const nom of noms) {
    const lu = lireNomDossier(nom)
    if (!lu) {
      ignores.push(nom)
      continue
    }
    const cle = cleRapprochement(lu.numeroSerie)
    if (vus.has(cle)) continue
    vus.add(cle)
    numeros.push(lu.numeroSerie)
    lus.push({ lu, dossier: nom })
  }

  const clesConnues = new Set(connus.map((e) => cleRapprochement(e.numeroSerie)))
  // 1 OF = 1 équipement : un OF déjà porté par un autre ne se réutilise pas.
  const ofsPris = new Map<string, string>()
  for (const e of connus) if (e.of.trim() !== '') ofsPris.set(cleRapprochement(e.of), e.numeroSerie)

  const aAjouter: AjoutDepuisDossier[] = []
  const nonAjoutes: AnalyseDossiers['nonAjoutes'] = []
  const horsReferentiel: string[] = []
  for (const { lu, dossier } of lus) {
    if (clesConnues.has(cleRapprochement(lu.numeroSerie))) continue
    if (lu.origine !== 'stock' && lu.origine !== 'vente') {
      horsReferentiel.push(lu.numeroSerie)
      continue
    }
    const ecarter = (motif: string): void => {
      nonAjoutes.push({ numeroSerie: lu.numeroSerie, dossier, motif })
    }
    const categorie = CATEGORIES_PAR_SUFFIXE[suffixeNumeroSerie(lu.numeroSerie)]
    if (!categorie) {
      ecarter('suffixe hors périmètre')
      continue
    }
    if (lu.origine === 'stock' && !lu.of) {
      ecarter('dossier STOCK sans OF')
      continue
    }
    const proprietaire = lu.of ? ofsPris.get(cleRapprochement(lu.of)) : undefined
    if (proprietaire) {
      ecarter(`${lu.of} est déjà l’OF de ${proprietaire} (1 OF = 1 équipement)`)
      continue
    }
    if (lu.of) ofsPris.set(cleRapprochement(lu.of), lu.numeroSerie)
    aAjouter.push({
      numeroSerie: lu.numeroSerie,
      origine: lu.origine,
      of: lu.of,
      pn: lu.origine === 'stock' ? PN_STOCK : '',
      cve: lu.cve,
      modele: modeleDuLibelleDossier(lu.libelle, categorie),
      libelle: lu.libelle,
      categorie,
      dossier,
    })
  }

  const cles = new Set(numeros.map(cleRapprochement))
  const clesActuelles = new Set(actuels.map(cleRapprochement))
  return {
    numeros,
    entrent: numeros.filter((n) => !clesActuelles.has(cleRapprochement(n))),
    sortent: actuels.filter((n) => !cles.has(cleRapprochement(n))),
    horsReferentiel,
    aAjouter,
    nonAjoutes,
    ignores,
  }
}

/** Lecture tolérante du bloc : un bloc absent ou abîmé = aucune liste. */
export function lireListeEnFabrication(brut: unknown): ListeEnFabrication | null {
  if (!brut || typeof brut !== 'object') return null
  const b = brut as Record<string, unknown>
  if (!Array.isArray(b.numerosSerie)) return null
  return {
    version: VERSION_EN_FABRICATION,
    numerosSerie: b.numerosSerie.filter((x): x is string => typeof x === 'string' && x.trim() !== ''),
    le: typeof b.le === 'string' ? b.le : '',
    par: typeof b.par === 'string' ? b.par : '',
  }
}

/** Clés de rapprochement des N° de série en fabrication, pour un test rapide. */
export function clesEnFabrication(liste: ListeEnFabrication | null): Set<string> {
  return new Set((liste?.numerosSerie ?? []).map(cleRapprochement))
}
